import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage } from "../../api/client";
import {
  blockedClients,
  calculations,
  imports,
  meta,
  operators as operatorsApi,
  records,
} from "../../api/endpoints";
import type { BlockedClientOut, Meta, RecordCreate, RecordOut } from "../../api/types";
import { ConfirmDialog, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Icon } from "../../components/Icon";
import { Button, ErrorNote, Field, Input, SectionTitle, Select, Spinner, Textarea } from "../../components/ui";
import { formatMoney, parseDecimal } from "../../lib/decimal";

export type FormMode = "create" | "edit" | "view" | "duplicate";

/** A position dated more than this many days before today gets a
 * confirmation prompt before it can be saved — mirrors the desktop dialog. */
const OLD_POSITION_DAYS = 30;

/** Today's date as YYYY-MM-DD in the browser's local timezone (not UTC). */
function todayIso(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/** True when `value` (YYYY-MM-DD) is more than OLD_POSITION_DAYS before today. */
function isOldPosition(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return false;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - OLD_POSITION_DAYS);
  return parsed < cutoff;
}

/** Same labels the desktop dialog uses, for readable validation messages. */
const FIELD_LABELS: Record<string, string> = {
  client_id: "Client ID",
  account_number: "Account Number",
  ticket_number: "Ticket Number",
  platform: "Platform",
  symbol: "Symbol",
  trade_type: "Type",
  position_date: "Position Date",
  sl_distance_override: "SL/Close Distance Override",
  lot: "Lot",
  open_price: "Open Price",
  expected_open_price: "Expected Open",
  tp_price: "TP",
  sl_price: "SL",
  close_price: "Close Price",
  profit: "Profit",
  compensation_amount: "Compensation Amount",
  reason: "Reason",
  command: "Command",
  notes: "Notes",
};

interface FormState {
  client_id: string;
  account_number: string;
  ticket_number: string;
  platform: string;
  operator_id: string;
  symbol: string;
  trade_type: string;
  position_date: string;
  sl_distance_override: boolean;
  lot: string;
  open_price: string;
  expected_open_price: string;
  tp_price: string;
  sl_price: string;
  close_price: string;
  profit: string;
  compensation_amount: string;
  reason: string;
  command: string;
  notes: string;
}

const BLANK: FormState = {
  client_id: "",
  account_number: "",
  ticket_number: "",
  platform: "",
  operator_id: "",
  symbol: "",
  trade_type: "",
  position_date: "",
  sl_distance_override: false,
  lot: "",
  open_price: "",
  expected_open_price: "",
  tp_price: "",
  sl_price: "",
  close_price: "",
  profit: "",
  compensation_amount: "",
  reason: "",
  command: "",
  notes: "",
};

function fromRecord(record: RecordOut, mode: FormMode): FormState {
  return {
    client_id: record.client_id,
    account_number: record.account_number,
    // A duplicate is a *different* ticket by definition — the dedup key is the
    // ticket number alone, so carrying it over would only ever be rejected.
    ticket_number: mode === "duplicate" ? "" : record.ticket_number,
    platform: record.platform,
    operator_id: record.operator_id === null ? "" : String(record.operator_id),
    symbol: record.symbol,
    trade_type: record.trade_type ?? "",
    position_date: record.position_date,
    sl_distance_override: record.sl_distance_override,
    lot: record.lot,
    open_price: record.open_price ?? "",
    expected_open_price: record.expected_open_price ?? "",
    tp_price: record.tp_price ?? "",
    sl_price: record.sl_price ?? "",
    close_price: record.close_price ?? "",
    profit: record.profit,
    compensation_amount: record.compensation_amount,
    reason: record.reason,
    command: record.command,
    notes: record.notes ?? "",
  };
}

/** Blank optional numbers are `null`, not `"0"` — "not provided" is meaningful. */
const optional = (text: string): string | null => (text.trim() === "" ? null : text.trim());

/** Every FormState field except the one boolean (sl_distance_override) —
 * these are the fields validateForm treats as text. */
type StringFormField = Exclude<keyof FormState, "sl_distance_override">;

const REQUIRED_TEXT_FIELDS: StringFormField[] = [
  "client_id",
  "account_number",
  "ticket_number",
  "symbol",
  "position_date",
];
const REQUIRED_SELECT_FIELDS: StringFormField[] = [
  "platform",
  "trade_type",
  "reason",
  "command",
];
/** field, required-if-blank. Expected Open is deliberately absent: like the
 * desktop, it's optional at the schema level even for the command that
 * reads it — leaving it blank fails at Calculate time, not at Save time. */
const NUMERIC_FIELDS: [StringFormField, boolean][] = [
  ["lot", true],
  ["profit", true],
  ["compensation_amount", true],
  ["open_price", false],
  ["tp_price", false],
  ["sl_price", false],
  ["close_price", false],
];

/**
 * The same class of check the desktop dialog does before ever building a
 * payload (parse_decimal per field, a combined message) — done here so a
 * blank required field or a typo like "12,34.5" is caught before a network
 * round trip and a raw server-side Pydantic message, rather than after one.
 * Returns null when the form is submittable.
 */
function validateForm(form: FormState): string | null {
  const problems: string[] = [];
  for (const field of REQUIRED_TEXT_FIELDS) {
    if (form[field].trim() === "") problems.push(`${FIELD_LABELS[field]} is required.`);
  }
  for (const field of REQUIRED_SELECT_FIELDS) {
    if (!form[field]) problems.push(`${FIELD_LABELS[field]} is required.`);
  }
  for (const [field, required] of NUMERIC_FIELDS) {
    const trimmed = form[field].trim();
    if (trimmed === "") {
      if (required) problems.push(`${FIELD_LABELS[field]} is required.`);
      continue;
    }
    if (parseDecimal(trimmed) === null) {
      problems.push(`${FIELD_LABELS[field]}: not a number.`);
    }
  }
  return problems.length > 0 ? problems.join(" ") : null;
}

const TITLES: Record<FormMode, string> = {
  create: "New Compensation Record",
  edit: "Edit Record",
  view: "Record",
  duplicate: "Duplicate Record",
};

export function RecordFormModal({
  open,
  mode,
  record,
  onClose,
  onSaved,
  onRequestEdit,
  onRequestDuplicate,
  onRequestDelete,
  onRequestDone,
}: {
  open: boolean;
  mode: FormMode;
  record: RecordOut | null;
  onClose: () => void;
  onSaved: () => void;
  onRequestEdit?: (record: RecordOut) => void;
  onRequestDuplicate?: (record: RecordOut) => void;
  onRequestDelete?: (record: RecordOut) => void;
  onRequestDone?: (record: RecordOut) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const readOnly = mode === "view";

  const metaQuery = useQuery({ queryKey: ["meta"], queryFn: () => meta.get(), staleTime: 300_000 });
  const operatorsQuery = useQuery({
    queryKey: ["operators", "active"],
    queryFn: () => operatorsApi.list(true),
    staleTime: 60_000,
  });
  const formulaQuery = useQuery({
    queryKey: ["formulas"],
    queryFn: () => calculations.formulas(),
    staleTime: 300_000,
  });

  const [form, setForm] = useState<FormState>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [quickPaste, setQuickPaste] = useState("");
  const [confirmOldPosition, setConfirmOldPosition] = useState(false);
  // Set when the client is on the blocked-from-compensation list; saving then
  // waits for the operator's explicit "Save Anyway".
  const [blockedEntry, setBlockedEntry] = useState<BlockedClientOut | null>(null);
  const [badge, setBadge] = useState("");
  const [calcWarning, setCalcWarning] = useState("");
  // Formula attribution, mirroring the desktop dialog: the amount is only
  // attributable to a formula while it is untouched since the last Calculate
  // (or since load, for an edit that never recalculated). Hand-typing over it
  // makes it "manual".
  const [attribution, setAttribution] = useState({ key: "manual", version: "1.0" });
  const [amountBaseline, setAmountBaseline] = useState("");
  // Only ever set from a server response (record load or Calculate) — never
  // derived client-side, since the FINAL rule ("Set Close on TP" replaces
  // rather than adds) lives in Python only. Stale after a manual edit to
  // Profit/Amount/Command until the next Calculate, same as the amount field.
  const [finalAmount, setFinalAmount] = useState<string | null>(null);

  const commandNames = metaQuery.data?.records.command_names ?? {};
  const zeroTradeCommand = commandNames["ZERO_TRADE"];
  const setOpenAsRequestedCommand = commandNames["SET_OPEN_AS_REQUESTED"];
  const setOpenAndCloseAsRequestedCommand = commandNames["SET_OPEN_AND_CLOSE_AS_REQUESTED"];
  const expectedOpenEnabled =
    !readOnly &&
    (form.command === setOpenAsRequestedCommand ||
      form.command === setOpenAndCloseAsRequestedCommand);

  // Reset whenever the modal is (re)opened, so a previous record's values never
  // bleed into the next one.
  useEffect(() => {
    if (!open) return;
    if (record) {
      setForm(fromRecord(record, mode));
      setAttribution({ key: record.formula_key, version: record.formula_version });
      setAmountBaseline(record.compensation_amount);
      setFinalAmount(record.final_amount);
      setBadge(`Stored amount computed by: ${record.formula_key} v${record.formula_version}`);
    } else {
      setForm({ ...BLANK, position_date: todayIso() });
      setAttribution({ key: "manual", version: "1.0" });
      setAmountBaseline("");
      setFinalAmount(null);
      setBadge("");
    }
    setCalcWarning("");
    setError(null);
    setQuickPaste("");
    setConfirmOldPosition(false);
  }, [open, record, mode]);

  // Defaults for a fresh record: first platform/reason/command, as the desktop
  // combo boxes do by starting on index 0.
  useEffect(() => {
    if (!open || record || !metaQuery.data) return;
    setForm((current) =>
      current.platform || current.reason || current.command
        ? current
        : {
            ...current,
            platform: metaQuery.data.records.platforms[0] ?? "",
            reason: metaQuery.data.records.reasons[0] ?? "",
            command: metaQuery.data.records.commands[0] ?? "",
          },
    );
  }, [open, record, metaQuery.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  function onCommandChange(next: string) {
    setForm((current) => {
      const updated = { ...current, command: next };
      // A stale Expected Open must not linger against a command that doesn't
      // use it.
      if (next !== setOpenAsRequestedCommand && next !== setOpenAndCloseAsRequestedCommand) {
        updated.expected_open_price = "";
      }
      if (next === zeroTradeCommand && !readOnly) updated.compensation_amount = "0.00";
      return updated;
    });
    if (next === zeroTradeCommand && !readOnly) {
      setAttribution({ key: "zero_trade", version: "—" });
      setAmountBaseline("0.00");
      setFinalAmount("0.00");
      setBadge("Zeroed: command is Zero the Trade and Correct the PnL");
    }
  }

  // -- quick paste -----------------------------------------------------------

  const tradeRow = useMutation({
    mutationFn: (text: string) => imports.tradeRow(text),
    onSuccess: (result) => {
      const fields = result.fields;
      if (Object.keys(fields).length === 0) {
        setQuickPaste("");
        toast.show("Pasted text didn't look like a trade row — nothing was filled.", "error");
        return;
      }
      setForm((current) => ({
        ...current,
        ticket_number: fields["ticket_number"] ?? current.ticket_number,
        position_date: fields["position_date"] ?? current.position_date,
        symbol: fields["symbol"] ?? current.symbol,
        trade_type: fields["trade_type"] ?? current.trade_type,
        lot: fields["lot"] ?? current.lot,
        open_price: fields["open_price"] ?? current.open_price,
        tp_price: fields["tp_price"] ?? current.tp_price,
        sl_price: fields["sl_price"] ?? current.sl_price,
        close_price: fields["close_price"] ?? current.close_price,
        profit: fields["profit"] ?? current.profit,
      }));
      setQuickPaste("");
      const filled = Object.keys(fields)
        .map((key) => FIELD_LABELS[key] ?? key)
        .join(", ");
      toast.success(`Filled from paste: ${filled}. Please double-check.`);
    },
    onError: (cause) => toast.error(errorMessage(cause)),
  });

  // -- calculate -------------------------------------------------------------

  const calculate = useMutation({
    mutationFn: () =>
      calculations.evaluate({
        open_price: optional(form.open_price),
        expected_open_price: expectedOpenEnabled ? optional(form.expected_open_price) : null,
        tp_price: optional(form.tp_price),
        sl_price: optional(form.sl_price),
        close_price: optional(form.close_price),
        profit: optional(form.profit),
        lot: optional(form.lot),
        symbol: form.symbol.trim() || null,
        command: form.command || null,
      }),
    onSuccess: (result) => {
      set("compensation_amount", result.amount);
      setAmountBaseline(result.amount);
      setFinalAmount(result.final_amount);
      setCalcWarning(result.warning ?? "");
      setAttribution({ key: result.formula_key, version: result.formula_version });
      setBadge(
        result.formula_key === "zero_trade"
          ? "Zeroed: command is Zero the Trade and Correct the PnL"
          : `Calculated by ${result.display_name} v${result.formula_version}`,
      );
    },
    onError: (cause) => setError(errorMessage(cause)),
  });

  // -- save ------------------------------------------------------------------

  /** Asks about a blocked client before saving. Editing a record that already
   * belongs to the client is not asked again — only moving it onto one is. */
  async function checkBlockedThenSave() {
    const unchanged =
      mode === "edit" && record !== null && form.client_id.trim() === record.client_id.trim();
    if (!unchanged) {
      try {
        const entry = await blockedClients.check(form.client_id);
        if (entry) {
          setBlockedEntry(entry);
          return;
        }
      } catch (cause) {
        setError(errorMessage(cause));
        return;
      }
    }
    save.mutate(false);
  }

  const payload = useMemo<RecordCreate>(() => {
    const untouched = form.compensation_amount === amountBaseline;
    return {
      client_id: form.client_id.trim(),
      account_number: form.account_number.trim(),
      ticket_number: form.ticket_number.trim(),
      platform: form.platform,
      symbol: form.symbol.trim(),
      trade_type: form.trade_type || null,
      position_date: form.position_date,
      sl_distance_override: form.sl_distance_override,
      lot: form.lot.trim(),
      open_price: optional(form.open_price),
      expected_open_price: expectedOpenEnabled ? optional(form.expected_open_price) : null,
      tp_price: optional(form.tp_price),
      sl_price: optional(form.sl_price),
      close_price: optional(form.close_price),
      profit: form.profit.trim(),
      compensation_amount: form.compensation_amount.trim(),
      reason: form.reason,
      command: form.command,
      operator_id: form.operator_id === "" ? null : Number(form.operator_id),
      formula_key: untouched ? attribution.key : "manual",
      formula_version: untouched ? attribution.version : "1.0",
      notes: form.notes.trim() || null,
      // Editing must not silently clear a tick someone already signed off.
      is_done: mode === "edit" ? (record?.is_done ?? false) : false,
      is_rejected: mode === "edit" ? (record?.is_rejected ?? false) : false,
    };
  }, [form, amountBaseline, attribution, expectedOpenEnabled, mode, record]);

  const save = useMutation({
    mutationFn: async (confirmBlocked: boolean) => {
      if (mode === "edit" && record) {
        return records.update(
          record.id,
          {
            ...payload,
            // Optimistic concurrency — 409 if someone else saved first.
            expected_updated_at: record.updated_at,
          },
          confirmBlocked,
        );
      }
      return records.create(payload, confirmBlocked);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["records"] });
      void queryClient.invalidateQueries({ queryKey: ["analytics"] });
      toast.success(
        mode === "edit" ? `Record #${saved.id} updated` : `Record #${saved.id} created`,
      );
      onSaved();
    },
    onError: (cause) => setError(errorMessage(cause)),
  });

  const auditQuery = useQuery({
    queryKey: ["records", "audit", record?.id],
    queryFn: () => records.auditTrail(record!.id),
    enabled: open && mode === "view" && record !== null,
  });

  const title = mode === "edit" && record ? `Edit Record #${record.id}` : record && mode === "view" ? `Record #${record.id}` : TITLES[mode];

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        size="lg"
        footer={
          readOnly ? (
            <>
              <div className="mr-auto flex gap-2">
                {onRequestDelete && record ? (
                  <Button variant="danger" onClick={() => onRequestDelete(record)}>
                    Delete
                  </Button>
                ) : null}
                {onRequestDone && record && !record.is_done ? (
                  <Button onClick={() => onRequestDone(record)}>
                    <Icon name="check" className="size-4 text-success" />
                    Done
                  </Button>
                ) : null}
              </div>
              {onRequestEdit && record ? (
                <Button onClick={() => onRequestEdit(record)}>Edit</Button>
              ) : null}
              {onRequestDuplicate && record ? (
                <Button onClick={() => onRequestDuplicate(record)}>Duplicate</Button>
              ) : null}
              <Button variant="primary" onClick={onClose}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const problem = validateForm(form);
                  if (problem) {
                    setError(problem);
                    return;
                  }
                  if (isOldPosition(form.position_date)) {
                    setConfirmOldPosition(true);
                    return;
                  }
                  void checkBlockedThenSave();
                }}
                disabled={save.isPending}
              >
                {save.isPending ? <Spinner /> : null}
                Save Record
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-4">
          {error ? <ErrorNote>{error}</ErrorNote> : null}

          {!readOnly ? (
            <div className="card p-3">
              <SectionTitle>Quick Paste</SectionTitle>
              <Input
                value={quickPaste}
                placeholder="Paste a trade row — Ticket, Pair, Type, Lot, Open Time, Open Price, TP, SL, Close Time, Close Price, Profit"
                onChange={(event) => {
                  const text = event.target.value;
                  setQuickPaste(text);
                  // A single value still being typed isn't a row yet; the desktop
                  // applies the same "needs at least two cells" guard.
                  if (text.trim().split(/[\t,;|]|\s{2,}/).filter(Boolean).length >= 2) {
                    tradeRow.mutate(text);
                  }
                }}
              />
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="card p-4">
              <SectionTitle>Identity</SectionTitle>
              <div className="flex flex-col gap-3">
                <Field label="Client ID">
                  <Input
                    value={form.client_id}
                    readOnly={readOnly}
                    onChange={(event) => set("client_id", event.target.value)}
                  />
                </Field>
                <Field label="Account">
                  <Input
                    value={form.account_number}
                    readOnly={readOnly}
                    onChange={(event) => set("account_number", event.target.value)}
                  />
                </Field>
                <Field label="Ticket">
                  <Input
                    value={form.ticket_number}
                    readOnly={readOnly}
                    onChange={(event) => set("ticket_number", event.target.value)}
                  />
                </Field>
                <Field label="Platform">
                  <Select
                    value={form.platform}
                    disabled={readOnly}
                    onChange={(event) => set("platform", event.target.value)}
                  >
                    {(metaQuery.data?.records.platforms ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Operator">
                  <Select
                    value={form.operator_id}
                    disabled={readOnly}
                    onChange={(event) => set("operator_id", event.target.value)}
                  >
                    <option value="">— none —</option>
                    {(operatorsQuery.data ?? []).map((operator) => (
                      <option key={operator.id} value={String(operator.id)}>
                        {operator.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>

            <div className="card p-4">
              <SectionTitle>Trade</SectionTitle>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Symbol">
                  <Input
                    value={form.symbol}
                    readOnly={readOnly}
                    onChange={(event) => set("symbol", event.target.value)}
                  />
                </Field>
                <Field label="Type">
                  <Select
                    value={form.trade_type}
                    disabled={readOnly}
                    onChange={(event) => set("trade_type", event.target.value)}
                  >
                    <option value="">— select —</option>
                    {(metaQuery.data?.records.trade_types ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Position Date"
                  hint="When this position happened on the client's live account — not necessarily today."
                >
                  <Input
                    type="date"
                    value={form.position_date}
                    readOnly={readOnly}
                    onChange={(event) => set("position_date", event.target.value)}
                  />
                </Field>
                <Field label="Lot">
                  <Input
                    inputMode="decimal"
                    value={form.lot}
                    readOnly={readOnly}
                    onChange={(event) => set("lot", event.target.value)}
                  />
                </Field>
                <Field label="Open Price">
                  <Input
                    inputMode="decimal"
                    value={form.open_price}
                    readOnly={readOnly}
                    onChange={(event) => set("open_price", event.target.value)}
                  />
                </Field>
                <Field
                  label="Expected Open"
                  hint={
                    expectedOpenEnabled
                      ? undefined
                      : "Used only by “Set the Open Price as Requested” or “Set the Open and Close Price as Requested and Correct the PnL”."
                  }
                >
                  <Input
                    inputMode="decimal"
                    value={form.expected_open_price}
                    readOnly={readOnly}
                    disabled={!expectedOpenEnabled && !readOnly}
                    onChange={(event) => set("expected_open_price", event.target.value)}
                  />
                </Field>
                <Field label="TP">
                  <Input
                    inputMode="decimal"
                    value={form.tp_price}
                    readOnly={readOnly}
                    onChange={(event) => set("tp_price", event.target.value)}
                  />
                </Field>
                <Field label="SL">
                  <Input
                    inputMode="decimal"
                    value={form.sl_price}
                    readOnly={readOnly}
                    onChange={(event) => set("sl_price", event.target.value)}
                  />
                </Field>
                <Field label="Close Price">
                  <Input
                    inputMode="decimal"
                    value={form.close_price}
                    readOnly={readOnly}
                    onChange={(event) => set("close_price", event.target.value)}
                  />
                </Field>
                <Field label="Profit (USD)">
                  <Input
                    inputMode="decimal"
                    value={form.profit}
                    readOnly={readOnly}
                    onChange={(event) => set("profit", event.target.value)}
                  />
                </Field>
                <Field
                  label="SL/Close Rule"
                  hint="Only needed if Close Price doesn't clear the minimum SL distance. Ticking it is recorded on the record."
                >
                  <label className="flex h-9 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 accent-accent"
                      checked={form.sl_distance_override}
                      disabled={readOnly}
                      onChange={(event) => set("sl_distance_override", event.target.checked)}
                    />
                    Save anyway
                  </label>
                </Field>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <SectionTitle>Compensation</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Reason">
                <Select
                  value={form.reason}
                  disabled={readOnly}
                  onChange={(event) => set("reason", event.target.value)}
                >
                  {(metaQuery.data?.records.reasons ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Command">
                <Select
                  value={form.command}
                  disabled={readOnly}
                  onChange={(event) => onCommandChange(event.target.value)}
                >
                  {(metaQuery.data?.records.commands ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className="sm:col-span-2">
                <Field label="Amount (USD)">
                  <div className="flex gap-2">
                    <Input
                      inputMode="decimal"
                      value={form.compensation_amount}
                      readOnly={readOnly}
                      onChange={(event) => set("compensation_amount", event.target.value)}
                    />
                    {!readOnly ? (
                      <Button
                        onClick={() => calculate.mutate()}
                        disabled={calculate.isPending}
                        title={
                          formulaQuery.data
                            ? describeActiveFormula(formulaQuery.data, metaQuery.data)
                            : undefined
                        }
                      >
                        {calculate.isPending ? <Spinner /> : <Icon name="calculator" />}
                        Calculate
                      </Button>
                    ) : null}
                  </div>
                </Field>
                {badge ? <p className="mt-1 text-xs text-fg-muted">{badge}</p> : null}
                {calcWarning ? <p className="mt-1 text-xs text-warning">⚠ {calcWarning}</p> : null}
              </div>

              <div className="sm:col-span-2">
                <Field label="Final (USD)">
                  <Input value={finalAmount === null ? "—" : formatMoney(finalAmount)} readOnly disabled />
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label="Notes">
                  <Textarea
                    rows={3}
                    value={form.notes}
                    readOnly={readOnly}
                    onChange={(event) => set("notes", event.target.value)}
                  />
                </Field>
              </div>
            </div>
          </div>

          {mode === "view" && record ? (
            <div className="card p-4">
              <SectionTitle>Audit Trail</SectionTitle>
              {auditQuery.isPending ? (
                <Spinner className="text-fg-muted" />
              ) : (auditQuery.data ?? []).length === 0 ? (
                <p className="text-sm text-fg-muted">No audit entries.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm text-fg-secondary">
                  {(auditQuery.data ?? []).slice(0, 8).map((entry, index) => (
                    <li key={entry.id ?? index}>
                      {formatAuditEntry(entry.created_at, entry.action, entry.operator_name)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </Modal>
      <ConfirmDialog
        open={confirmOldPosition}
        title="Old Position"
        message={`This position is dated ${form.position_date}, more than ${OLD_POSITION_DAYS} days ago. Are you sure you want to save it?`}
        confirmLabel="Save Anyway"
        busy={save.isPending}
        onConfirm={() => {
          setConfirmOldPosition(false);
          void checkBlockedThenSave();
        }}
        onCancel={() => setConfirmOldPosition(false)}
      />
      <ConfirmDialog
        open={blockedEntry !== null}
        title="Blocked Client"
        message={
          blockedEntry
            ? `Client ${blockedEntry.client_id} is blocked from slippage compensation.` +
              (blockedEntry.reason ? ` Reason: ${blockedEntry.reason}.` : "") +
              " Are you sure you want to save this record anyway?"
            : ""
        }
        confirmLabel="Save Anyway"
        danger
        busy={save.isPending}
        onConfirm={() => {
          setBlockedEntry(null);
          save.mutate(true);
        }}
        onCancel={() => setBlockedEntry(null)}
      />
    </>
  );
}

function describeActiveFormula(
  list: { formulas: { key: string; display_name: string; description: string }[]; active_key: string },
  _meta: Meta | undefined,
): string {
  const active = list.formulas.find((formula) => formula.key === list.active_key);
  return active ? `${active.display_name}: ${active.description}` : "Calculate compensation";
}

function formatAuditEntry(createdAt: string, action: string, operatorName: string | null): string {
  const when = new Date(createdAt);
  const stamp = Number.isNaN(when.getTime()) ? createdAt : when.toLocaleString();
  return `${stamp} — ${action} by ${operatorName ?? "system"}`;
}
